<template>
  <div class="SavedPostCard">
    <!-- Bookmark (remove from saved) -->
    <LLButton tertiary-dark small :loading="isUnsaving" class="SavedPostCard__bookmark" @click.stop="unsave">
      <template #icon-left>
        <SavedPostsFilledIcon class="SavedPostCard__bookmark-icon" />
      </template>
    </LLButton>

    <!-- Top row: avatar + name + date -->
    <div class="SavedPostCard__top">
      <div class="SavedPostCard__avatar-wrap">
        <ForumResponseAvatar
          v-if="isResponse && responseModel"
          :response="responseModel"
          :chat-id="chatId"
          class="SavedPostCard__avatar"
        />
        <LogoRound v-else-if="isThread && threadIsModerator" class="SavedPostCard__avatar-mod" />
        <div v-else-if="isThread && threadIsAnonymous" class="SavedPostCard__avatar-anon">
          {{ anonymousLetter }}
        </div>
        <div v-else class="SavedPostCard__avatar-user">
          {{ creatorInitial }}
        </div>
      </div>
      <div class="SavedPostCard__who-wrap">
        <div class="SavedPostCard__who">
          <template v-if="isResponse && responseModel">
            <ForumResponseProfile :response="responseModel" :hide-notice="true" />
          </template>
          <template v-else-if="isThread && threadIsModerator">
            <span class="text-accent-01">{{ $t('forum.response.moderator_reply_user_name') }}</span>
          </template>
          <template v-else-if="isThread && threadIsAnonymous">
            <span class="text-primary-01-400">{{ $t('forum.response.anonymous_reply_user_name') }}</span>
          </template>
          <template v-else>
            {{ creatorName }}
          </template>
        </div>
        <LLTimestamp :timestamp="postedAt" class="SavedPostCard__when">
          <template #default="data">{{ $t('forum.saved_page.posted_label') }} {{ data.time }}</template>
        </LLTimestamp>
      </div>
    </div>

    <!-- Body -->
    <div class="SavedPostCard__body">
      <template v-if="isResponse">
        <ForumResponseBodyTextOutput :text="responseText" class="SavedPostCard__response-text" />
        <div v-if="responseFiles.length && tailwindScreens.init" class="SavedPostCard__files">
          <LLFileSlider :owner="owner" :medium="tailwindScreens.lg" :files="responseFiles" />
        </div>
      </template>
      <template v-else>
        <router-link :to="threadLink" class="SavedPostCard__title-link">
          <!-- eslint-disable-next-line vue/no-v-html -->
          <span v-if="highlightText" v-html="highlightText" />
          <span v-else>{{ threadTitle }}</span>
        </router-link>
        <LLWysiwygOutput v-if="threadText" :text="threadText" class="SavedPostCard__thread-text" />
      </template>
    </div>

    <!-- Footer -->
    <div class="SavedPostCard__foot">
      <router-link :to="threadLink" class="SavedPostCard__see-in">
        {{ isThread ? $t('forum.saved_page.open_thread') : $t('forum.saved_page.see_in_thread') }}
      </router-link>
      <LLTimestamp :timestamp="savedAt" class="SavedPostCard__saved-stamp">
        <template #default="data">{{ $t('forum.saved_page.saved_label') }} {{ data.time }}</template>
      </LLTimestamp>
    </div>

    <!-- Removed overlay -->
    <transition name="SavedPostCard__overlay-fade">
      <div v-if="isRemoved" class="SavedPostCard__overlay">
        <span class="SavedPostCard__overlay-text">{{ $t('forum.saved_page.removed_from_saved') }}</span>
        <LLButton secondary small class="SavedPostCard__undo-btn" @click.stop="onUndo">
          {{ $t('forum.saved_page.undo') }}
        </LLButton>
      </div>
    </transition>
  </div>
</template>

<script>
import { ForumResponseModel } from '@/models/forum/forumResponse'
import ForumResponseAvatar from '@/components/pages/forum/response/ForumResponseAvatar.vue'
import ForumResponseProfile from '@/components/pages/forum/response/ForumResponseProfile.vue'
import ForumResponseBodyTextOutput from '@/components/pages/forum/response/body/text/ForumResponseBodyTextOutput.vue'
import LLTimestamp from '@/components/common/LLTimestamp'
import LLButton from '@/components/common/ui-components/LLButton'
import LLWysiwygOutput from '@/components/common/LLWysiwygOutput.vue'
import LLFileSlider from '@/components/common/LLFileSlider'
import SavedPostsFilledIcon from '@/assets/icons/SavedPostsFilledIcon.vue'
import LogoRound from '@/assets/icons/LogoRound.vue'
import { screenCatcher } from '@/mixins/screenCatcher'
import { ownerFromForumModel } from '@/utils/fileOwner'

export default {
  name: 'SavedPostCard',
  components: {
    ForumResponseAvatar,
    ForumResponseProfile,
    ForumResponseBodyTextOutput,
    LLTimestamp,
    LLButton,
    LLWysiwygOutput,
    LLFileSlider,
    SavedPostsFilledIcon,
    LogoRound
  },
  mixins: [screenCatcher],
  props: {
    item: { type: Object, required: true },
    chatId: { type: [Number, String], required: true }
  },
  data() {
    return {
      isUnsaving: false,
      isRemoved: false,
      undoTimer: null
    }
  },
  computed: {
    isThread() {
      return this.item.type === 'chat_threads'
    },
    isResponse() {
      return this.item.type === 'chat_responses'
    },

    // ── Thread card ──
    threadTitle() {
      return this.item?.thread?.title || ''
    },
    threadText() {
      return this.item?.thread?.text || ''
    },
    highlightText() {
      const hl = this.item?.highlight?.text
      if (!hl || !hl.length) return null
      return hl.map((t) => `<em>${t}</em>`).join(' … ')
    },
    threadIsAnonymous() {
      return !!(this.item?.anonymousState && !this.item?.isOwnerModerator)
    },
    threadIsModerator() {
      return !!(this.item?.anonymousState && this.item?.isOwnerModerator)
    },
    threadCreator() {
      return this.item?.user || this.item?.prospect || null
    },
    creatorName() {
      const c = this.threadCreator
      if (!c) return this.$t('not-available')
      const first = c.firstName || ''
      const last = c.lastName || ''
      if (first || last) return `${first} ${last.charAt(0)}.`.trim()
      return c.email || c.phone || this.$t('not-available')
    },
    creatorInitial() {
      const c = this.threadCreator
      if (!c) return '?'
      const name = c.firstName || c.lastName || c.email || ''
      return name.charAt(0).toUpperCase()
    },

    // ── Response card ──
    responseModel() {
      if (!this.isResponse) return null
      try {
        return ForumResponseModel.parseFromApi({
          response: this.item.response,
          chatId: this.item.chat?.id,
          threadId: this.item.thread?.id,
          users: this.item.users,
          prospects: this.item.prospects
        })
      } catch {
        return null
      }
    },
    responseText() {
      return this.responseModel?.mentionProcessedText || this.item?.response?.text || ''
    },
    responseFiles() {
      return this.responseModel?.files || []
    },
    owner() {
      return ownerFromForumModel({
        model: this.isResponse ? this.responseModel : this.item,
        currentUserId: this.$store.getters['user/user']?.id
      })
    },

    // ── Common ──
    threadId() {
      return this.item?.thread?.id || ''
    },
    threadLink() {
      let link = `/chats/${this.chatId}/threads/${this.threadId}`
      if (this.isResponse && this.item.response?.id) {
        link += `?goToResponseId=${this.item.response.id}`
      }
      return link
    },
    postedAt() {
      const date = this.isThread ? this.item?.thread?.createdAt : this.item?.response?.createdAt
      return date ? new Date(date).getTime() : 0
    },
    savedAt() {
      const ts = this.item?.savedAt
      return ts ? new Date(ts).getTime() : 0
    },
    anonymousLetter() {
      return this.$t('forum.response.anonymous_reply_user_name').slice(0, 1)
    }
  },
  beforeDestroy() {
    this._clearTimer()
  },
  methods: {
    async unsave() {
      if (this.isUnsaving) return
      this.isUnsaving = true
      try {
        await this._toggleSave()
        this.isRemoved = true
        this.$emit('removed', { item: this.item })
        this._startTimer()
      } catch (e) {
        // silently fail
      }
      this.isUnsaving = false
    },
    async onUndo() {
      if (this.isUnsaving) return
      this.isUnsaving = true
      try {
        await this._toggleSave()
        this.isRemoved = false
        this._clearTimer()
        this.$emit('restored', { item: this.item })
      } catch (e) {
        // silently fail
      }
      this.isUnsaving = false
    },
    _toggleSave() {
      if (this.isThread) {
        return this.$api.forum.savedPosts.toggleThreadSave({
          chatId: this.chatId,
          threadId: this.threadId
        })
      }
      return this.$api.forum.savedPosts.toggleResponseSave({
        chatId: this.chatId,
        threadId: this.threadId,
        responseId: this.item.response?.id
      })
    },
    _startTimer() {
      this._clearTimer()
      this.undoTimer = setTimeout(() => {
        this.$emit('dismissed', { item: this.item })
      }, 5000)
    },
    _clearTimer() {
      if (this.undoTimer) {
        clearTimeout(this.undoTimer)
        this.undoTimer = null
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.SavedPostCard {
  $root: &;
  position: relative;
  @apply py-4 px-4 border border-neutral-01-50 rounded bg-white;
  @screen md {
    @apply py-5 px-5;
  }

  &__bookmark {
    @apply absolute top-3 right-3;
  }
  &__bookmark-icon {
    @apply text-primary-01;
  }

  &__top {
    @apply flex items-start gap-3;
  }

  &__avatar-wrap {
    @apply flex-shrink-0 mt-0.5;
  }
  &__avatar {
    @apply w-8 h-8;
  }
  &__avatar-mod {
    @apply w-8 h-8 rounded-full;
  }
  &__avatar-anon {
    @apply w-8 h-8 rounded-full bg-neutral-01-400 text-white flex items-center justify-center text-body-03;
  }
  &__avatar-user {
    @apply w-8 h-8 rounded-full bg-neutral-01-300 text-white flex items-center justify-center text-body-03 font-bold;
  }

  &__who-wrap {
    @apply min-w-0 flex-1;
  }
  &__who {
    @apply text-body-02 pr-10;
  }
  &__when {
    @apply text-neutral-01-400 inline-block;
    ::v-deep .LLTimestamp {
      font-size: 12px;
    }
  }

  &__body {
    @apply mt-2 ml-11 text-body-03;
  }
  &__title-link {
    @apply text-primary-01-400 font-bold;
  }
  &__thread-text {
    @apply mt-1;
  }
  &__response-text {
  }
  &__files {
    @apply mt-2;
  }

  &__foot {
    @apply flex items-center gap-4 mt-2 ml-11 text-caption-02;
  }
  &__see-in {
    @apply text-primary-01-400 font-semibold;
  }
  &__saved-stamp {
    @apply text-neutral-01-400;
    ::v-deep .LLTimestamp {
      font-size: 12px;
    }
  }

  &__overlay {
    @apply absolute inset-0 bg-white bg-opacity-80 flex flex-col items-center justify-center gap-2 z-10 rounded;
  }
  &__overlay-text {
    @apply text-body-03 text-neutral-01-400;
  }
  &__overlay-fade-enter-active,
  &__overlay-fade-leave-active {
    transition: opacity 0.2s ease;
  }
  &__overlay-fade-enter,
  &__overlay-fade-leave-to {
    opacity: 0;
  }
}
</style>
